import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { useResumeContext } from "./contexts/ResumeContext";
import { downloadPdf } from "../utils/helpers";
import { Download, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "./ui/alert";

// Template components
import BaseTemplate from "./resume-templates/BaseTemplate";
import FresherTemplate from "./resume-templates/FresherTemplate";
import ExperiencedTemplate from "./resume-templates/ExperiencedTemplate";
import SeniorTemplate from "./resume-templates/SeniorTemplate";
import LeadTemplate from "./resume-templates/LeadTemplate";

// A4 dimensions in pixels (at 96 DPI)
const A4_HEIGHT_PX = 1123; // 297mm
const A4_MARGINS_PX = 75.6; // 20mm

// Define templates object
const templates = {
  base: BaseTemplate,
  fresher: FresherTemplate,
  experienced: ExperiencedTemplate,
  senior: SeniorTemplate,
  lead: LeadTemplate,
};

const ResumePreview = () => {
  const { resumeData, templateType } = useResumeContext();
  const contentRef = useRef(null);
  const wrapperRef = useRef(null);
  const [overflowStatus, setOverflowStatus] = useState({
    hasOverflow: false,
    scale: 1,
  });

  // Get the correct template component
  const TemplateComponent = templates[templateType] || templates.base;

  useEffect(() => {
    const checkOverflow = () => {
      if (!contentRef.current || !wrapperRef.current) return;

      const content = contentRef.current;
      const wrapper = wrapperRef.current;

      // Reset any previous scaling and styles
      content.style.transform = "scale(1)";
      content.style.marginBottom = "0";

      // Get the actual content height
      const contentHeight = content.getBoundingClientRect().height;
      const maxHeight = A4_HEIGHT_PX - 2 * A4_MARGINS_PX; // Account for margins

      if (contentHeight > maxHeight) {
        // Calculate scale needed to fit content
        const scale = maxHeight / contentHeight;

        setOverflowStatus({
          hasOverflow: true,
          scale: scale,
        });

        // Apply scaling
        content.style.transform = `scale(${scale})`;
        content.style.transformOrigin = "top center";

        // Adjust wrapper height to match scaled content
        const scaledHeight = contentHeight * scale;
        wrapper.style.height = `${scaledHeight + 2 * A4_MARGINS_PX}px`;
      } else {
        setOverflowStatus({
          hasOverflow: false,
          scale: 1,
        });

        // Set wrapper height to match content exactly
        wrapper.style.height = `${contentHeight + 2 * A4_MARGINS_PX}px`;
      }
    };

    // Initial check with a delay to ensure content is rendered
    const initialCheckTimeout = setTimeout(checkOverflow, 100);

    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    // Clean up
    return () => {
      clearTimeout(initialCheckTimeout);
      resizeObserver.disconnect();
    };
  }, [resumeData, templateType]);

  const handleDownload = () => {
    if (!contentRef.current || !wrapperRef.current) return;

    const wrapper = wrapperRef.current;
    const content = contentRef.current;

    // Store original styles
    const originalWrapperHeight = wrapper.style.height;
    const originalContentTransform = content.style.transform;

    // Reset styles for PDF generation
    wrapper.style.height = "auto";
    content.style.transform = "scale(1)";

    const fileName = `${resumeData.personalInfo.fullName.replace(
      /\s+/g,
      "_"
    )}_Resume.pdf`;
    downloadPdf("resume-preview", fileName);

    // Restore original styles
    wrapper.style.height = originalWrapperHeight;
    content.style.transform = originalContentTransform;
  };

  return (
    <div className="p-4 flex flex-col h-full">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-4 mb-2">
        <h2 className="text-2xl font-bold">Resume Preview</h2>
        <Button
          onClick={handleDownload}
          className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
        >
          <Download className="mr-2 h-4 w-4 text-white" />
          <span className="whitespace-nowrap">Download PDF</span>
        </Button>
      </div>

      {overflowStatus.hasOverflow && (
        <Alert variant="warning" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Content exceeds one page. The resume has been scaled to{" "}
            {Math.round(overflowStatus.scale * 100)}% to fit. Consider removing
            some content or making it more concise.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex-1 overflow-auto p-2">
        <div
          ref={wrapperRef}
          className="w-[210mm] bg-white print:shadow-none relative mx-auto"
          id="resume-preview"
          style={{
            padding: "5mm",
            minHeight: "fit-content",
            maxHeight: "297mm",
            pageBreakAfter: "avoid",
            pageBreakInside: "avoid",
          }}
        >
          <div
            ref={contentRef}
            className="max-w-full print:max-w-none transform-gpu"
            style={{
              transformOrigin: "top center",
              pageBreakInside: "avoid",
              pageBreakAfter: "avoid",
            }}
          >
            <TemplateComponent data={resumeData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumePreview;
